<template>
  <section class="admin-content" id="all-claims-page">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="row align-items-center p-t-40 p-b-40">
            <div class="col-md-8 text-white">
              <h2 class="page-title"><i class="fe fe-file-text mr-2"></i>Claims</h2>
              <p class="page-subtitle">Review and manage submitted claims</p>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- ── STAT CARDS ───────────────────────────────── -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3">
            <div class="stat-card stat-card--blue">
              <div class="stat-icon">
                <i class="fe fe-file-text"></i>
              </div>
              <div>
                <p class="stat-label">Total Claims</p>
                <h3 class="stat-value">{{ stats.total | numeral(0, 0) }}</h3>
              </div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="stat-card stat-card--purple">
              <div class="stat-icon">
                <i class="fe fe-credit-card"></i>
              </div>
              <div>
                <p class="stat-label">Amount Requested</p>
                <h3 class="stat-value">
                  <i class="mdi mdi-currency-ngn"></i>{{ totalRequestedCost | numeral(0, 0) }}
                </h3>
              </div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="stat-card stat-card--green">
              <div class="stat-icon">
                <i class="fe fe-check-circle"></i>
              </div>
              <div>
                <p class="stat-label">Amount Approved</p>
                <h3 class="stat-value">
                  <i class="mdi mdi-currency-ngn"></i>{{ totalApprovedCost | numeral(0, 0) }}
                </h3>
              </div>
            </div>
          </div>
          <div class="col-12">
            <small class="text-muted">* Data from {{ from }} to {{ to }}</small>
          </div>
        </div>

        <!-- ── FILTER CARD ──────────────────────────────── -->
        <div class="card filter-card shadow-sm mb-4" v-show="filterparams">
          <div class="card-body py-3 px-4">
            <p class="font-weight-bold mb-3 text-secondary small text-uppercase" style="letter-spacing: .05em;">
              <i class="fe fe-filter mr-1"></i> Filter Claims
            </p>
            <div class="row align-items-end">
              <div class="form-group col-md-3 mb-2">
                <label class="small text-muted mb-1">Sector</label>
                <select class="form-control form-control-sm styled-select" v-model="sector">
                  <option value="">All Sectors</option>
                  <option :value="s" v-for="s in sectors" :key="s">{{ s }}</option>
                </select>
              </div>
              <div class="form-group col-md-3 mb-2">
                <label class="small text-muted mb-1">Authorization Code</label>
                <input type="text" class="form-control form-control-sm" v-model="authorization_code" placeholder="AUT_xxxxxxxx" />
              </div>
              <div class="form-group col-md-3 mb-2">
                <label class="small text-muted mb-1">{{ agency.firstname }} Number</label>
                <input type="text" class="form-control form-control-sm" v-model="id_card_number" placeholder="Enrollee ID…" />
              </div>
              <div class="form-group col-md-3 mb-2">
                <label class="small text-muted mb-1">Status</label>
                <select class="form-control form-control-sm styled-select" v-model="claim_status">
                  <option value="">All</option>
                  <option value="approved">Approved</option>
                  <option value="vetted">Vetted</option>
                  <option value="verified">Verified</option>
                  <option value="forwarded">Forwarded to ES</option>
                  <option value="pending">Pending</option>
                  <option value="draft" v-if="user && (user.type == 'provider' || user.type == 'provider_employee')">Draft</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div class="form-group col-md-4 mb-2" v-if="user && (user.type == 'shis' || user.type == 'employee')">
                <label class="small text-muted mb-1">Facility</label>
                <div class="d-flex align-items-center" style="gap: 8px;">
                  <div class="flex-grow-1">
                    <v-select v-model="provider_id" label="agency_name" :options="providers" @change="getClaims" />
                  </div>
                  <button @click="getProviders" class="btn btn-outline-info btn-sm" style="white-space: nowrap;">
                    All
                  </button>
                </div>
              </div>
              <div class="form-group col-md-2 mb-2">
                <label class="small text-muted mb-1">Start Date</label>
                <input type="date" class="form-control form-control-sm" v-model="from" />
              </div>
              <div class="form-group col-md-2 mb-2">
                <label class="small text-muted mb-1">End Date</label>
                <input type="date" class="form-control form-control-sm" v-model="to" />
              </div>
              <div class="form-group col-md-2 mb-2">
                <label class="small text-muted mb-1">Rows per Page</label>
                <select class="form-control form-control-sm styled-select" v-model.number="paginate_value">
                  <option :value="15">15</option>
                  <option :value="30">30</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                  <option :value="200">200</option>
                  <option :value="500">500</option>
                  <option :value="1000">1000</option>
                </select>
              </div>
            </div>
            <div class="d-flex align-items-center mt-1" style="gap: 8px;">
              <button type="button" class="btn btn-dark btn-sm px-4" @click="getClaims(1)">
                <i class="fe fe-filter mr-1"></i> Apply
              </button>
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearField">
                <i class="fe fe-x mr-1"></i> Clear
              </button>
            </div>
          </div>
        </div>

        <!-- ── CLAIMS TABLE CARD ────────────────────────── -->
        <div class="card codes-card shadow-sm">
          <div class="card-header-custom">
            <div class="d-flex align-items-center" style="gap: 10px;">
              <h5 class="mb-0"><i class="fe fe-list mr-2"></i>Claims List</h5>
              <span class="badge badge-pill badge-secondary">{{ pagination.total }} records</span>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px;">
              <div class="view-toggle">
                <button :class="['view-toggle-btn', viewMode === 'list' ? 'active' : '']" @click="viewMode = 'list'" title="List view"><i class="fe fe-list"></i></button>
                <button :class="['view-toggle-btn', viewMode === 'grid' ? 'active' : '']" @click="viewMode = 'grid'" title="Grid view"><i class="fe fe-grid"></i></button>
              </div>
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="filterparams = !filterparams">
                <i class="fe fe-sliders mr-1"></i> {{ filterparams ? 'Hide' : 'Filters' }}
              </button>
              <button class="btn btn-outline-success btn-sm">
                <download-excel :data="claims.data" :fields="json_fields" type="xls"
                  :escapeCsv="false" :name="'claims_data.xls'">
                  <i class="fe fe-download mr-1"></i> Export
                </download-excel>
              </button>
              <button class="btn btn-outline-info btn-sm" v-if="user && (user.type == 'shis' || user.type == 'employee')">
                <download-excel :data="providers" :fields="provider_fields" type="xls"
                  :escapeCsv="false" :name="'providers.xls'">
                  <i class="fe fe-download mr-1"></i> Providers
                </download-excel>
              </button>
              <button class="btn btn-outline-primary btn-sm" v-if="user && user.user_role == 1" @click="openPerformanceReport">
                <i class="fe fe-bar-chart-2 mr-1"></i> Employee Performance
              </button>
            </div>
          </div>

          <div class="card-body p-0" v-if="viewMode === 'list'">
            <div class="table-responsive">
              <table class="table codes-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Facility</th>
                    <th>Patient</th>
                    <th>Diagnosis</th>
                    <th>Requested / Approved</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!claims.data || !claims.data.length">
                    <td colspan="8" class="text-center empty-state">
                      <i class="fe fe-file-text" style="font-size: 2rem; opacity: 0.3;"></i>
                      <p class="mt-2 mb-0">No claims found for the selected filters</p>
                    </td>
                  </tr>
                  <tr v-for="(claim, index) in claims.data" :key="claim.id" class="code-row">
                    <td class="text-muted" style="font-size: 0.8rem;">{{ (pagination.current_page - 1) * pagination.per_page + index + 1 }}</td>
                    <td>
                      <span v-if="claim.provider" class="font-weight-medium" style="font-size: 0.85rem;">
                        {{ claim.provider.agency_name }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <div v-if="claim.enrollee">
                        <p class="mb-0 font-weight-medium" style="font-size: 0.85rem;">{{ claim.enrollee.full_name }}</p>
                        <small class="text-muted">({{claim.enrollee.sector}})  {{ claim.enrollee.id_card_number }}</small>
                      </div>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td style="font-size: 0.85rem;">
                      <span v-if="claim.diagnosis">{{ claim.diagnosis.name }}</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td style="font-size: 0.85rem;">
                      <span class="d-block text-dark">
                        <i class="mdi mdi-currency-ngn"></i>{{ claim.requested_total_cost | numeral(0, 0) }}
                      </span>
                      <span class="text-success font-weight-bold">
                        <i class="mdi mdi-currency-ngn"></i>{{ claim.approved_total_cost | numeral(0, 0) }}
                      </span>
                    </td>
                    <td>
                      <span class="status-badge" :class="getClaimStatus(claim).variant">
                        <i :class="['fe', 'mr-1', getClaimStatus(claim).icon]"></i>{{ getClaimStatus(claim).label }}
                      </span>
                    </td>
                    <td>
                      <span class="d-block" style="font-size: 0.83rem;">{{ claim.created_at | moment("MMM Do YYYY") }}</span>
                      <small class="text-muted">{{ claim.created_at | moment("from", "now") }}</small>
                    </td>
                    <td class="text-right">
                      <button type="button" class="btn btn-sm btn-view" @click="openClaim(claim)">
                        <i class="fe fe-eye mr-1"></i>View
                      </button>
                    </td>
                  </tr>

                  <!-- Forward claims row -->
                  <tr v-if="user && user.job_title == 'Claims Verifier' && provider_id && claims.data.length">
                    <td colspan="8" class="py-3 px-4">
                      <b-alert show variant="primary" class="mb-2">
                        To forward claims to the ES for approval, select a facility above.
                      </b-alert>
                      <button class="btn btn-dark" @click="forwwardClaims()">
                        <i class="fe fe-send mr-2"></i>
                        Forward all of {{ provider_id.agency_name }}'s verified claims to the ES for approval
                      </button>
                    </td>
                  </tr>
                  <tr v-else-if="user && user.job_title == 'Claims Verifier' && !provider_id">
                    <td colspan="8" class="px-4 pt-0 pb-3">
                      <b-alert show variant="primary" class="mb-0">
                        To forward claims to the ES for approval, select a facility above.
                      </b-alert>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="claims-grid" v-if="viewMode === 'grid'">
            <div v-if="!claims.data || !claims.data.length" style="grid-column:1/-1" class="text-center empty-state">
              <i class="fe fe-file-text" style="font-size:2rem;opacity:0.3"></i>
              <p class="mt-2 mb-0">No claims found for the selected filters</p>
            </div>
            <div v-for="claim in claims.data" :key="claim.id" class="claim-grid-card">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <div class="font-weight-bold" style="font-size:.88rem;">{{ claim.enrollee ? claim.enrollee.full_name : '—' }}</div>
                  <small class="text-muted">{{ claim.enrollee ? claim.enrollee.id_card_number : '' }}</small>
                </div>
                <small class="text-muted">{{ claim.created_at | moment("MMM Do") }}</small>
              </div>
              <div class="mb-2" style="font-size:.85rem;">
                <span v-if="claim.diagnosis">{{ claim.diagnosis.name }}</span>
                <span v-else class="text-muted">No diagnosis</span>
              </div>
              <div class="d-flex mb-2" style="gap:8px; font-size:.82rem;">
                <span class="text-muted">Req: <strong><i class="mdi mdi-currency-ngn"></i>{{ claim.requested_total_cost | numeral(0,0) }}</strong></span>
                <span class="text-success">Appr: <strong><i class="mdi mdi-currency-ngn"></i>{{ claim.approved_total_cost | numeral(0,0) }}</strong></span>
              </div>
              <div class="d-flex justify-content-between align-items-center grid-card-footer">
                <div>
                  <span class="status-badge" :class="getClaimStatus(claim).variant">
                    <i :class="['fe', 'mr-1', getClaimStatus(claim).icon]"></i>{{ getClaimStatus(claim).label }}
                  </span>
                </div>
                <button type="button" class="btn btn-sm btn-view" @click="openClaim(claim)">
                  <i class="fe fe-eye mr-1"></i>View
                </button>
              </div>
            </div>
          </div>

          <!-- ── Pagination ───────────────────────────────── -->
          <div class="card-footer d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px;" v-if="pagination.last_page > 1">
            <small class="text-muted">
              Showing {{ (pagination.current_page - 1) * pagination.per_page + 1 }}–{{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}
              of {{ pagination.total }} claims
            </small>
            <nav>
              <ul class="pagination pagination-sm mb-0">
                <!-- Prev -->
                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                  <button class="page-link" @click="getClaims(pagination.current_page - 1)">
                    <i class="fe fe-chevron-left"></i>
                  </button>
                </li>

                <!-- First page -->
                <li class="page-item" :class="{ active: pagination.current_page === 1 }">
                  <button class="page-link" @click="getClaims(1)">1</button>
                </li>

                <!-- Left ellipsis -->
                <li class="page-item disabled" v-if="pagination.current_page > 3">
                  <span class="page-link">…</span>
                </li>

                <!-- Middle pages -->
                <template v-for="p in pagination.last_page">
                  <li
                    v-if="p > 1 && p < pagination.last_page && Math.abs(p - pagination.current_page) <= 1"
                    :key="p"
                    class="page-item"
                    :class="{ active: p === pagination.current_page }"
                  >
                    <button class="page-link" @click="getClaims(p)">{{ p }}</button>
                  </li>
                </template>

                <!-- Right ellipsis -->
                <li class="page-item disabled" v-if="pagination.current_page < pagination.last_page - 2">
                  <span class="page-link">…</span>
                </li>

                <!-- Last page -->
                <li class="page-item" :class="{ active: pagination.current_page === pagination.last_page }" v-if="pagination.last_page > 1">
                  <button class="page-link" @click="getClaims(pagination.last_page)">{{ pagination.last_page }}</button>
                </li>

                <!-- Next -->
                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                  <button class="page-link" @click="getClaims(pagination.current_page + 1)">
                    <i class="fe fe-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>

      </div>

      <!-- ── Employee Performance Report Modal ─────────────────── -->
      <div class="perf-modal-overlay" v-if="perfReport.open" @click.self="closePerformanceReport">
        <div class="card shadow perf-modal">
          <div class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
            <div>
              <h6 class="mb-0 font-weight-bold">Employee Performance — Claims</h6>
              <small class="text-muted">{{ from }} to {{ to }}</small>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px;">
              <button
                class="btn btn-outline-success btn-sm"
                v-if="!perfReport.loading && perfReport.rows.length"
              >
                <download-excel :data="perfReport.rows" :fields="perf_fields" type="xls"
                  :escapeCsv="false" :name="'claims_employee_performance.xls'">
                  <i class="fe fe-download mr-1"></i> Download Excel
                </download-excel>
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="closePerformanceReport">
                <i class="fe fe-x"></i>
              </button>
            </div>
          </div>
          <div class="px-4 py-3 perf-modal-body">
            <div v-if="perfReport.loading" class="text-center text-muted py-5">
              <i class="fe fe-loader"></i> Loading…
            </div>
            <div v-else-if="!perfReport.rows.length" class="text-center text-muted py-5">
              No vetting/verification activity found in this date range
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th class="text-right">Vetted</th>
                    <th class="text-right">Verified</th>
                    <th class="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in perfReport.rows" :key="row.employee_id">
                    <td>{{ row.employee_name }}</td>
                    <td class="text-right">{{ row.vetted }}</td>
                    <td class="text-right">{{ row.verified }}</td>
                    <td class="text-right font-weight-bold">{{ row.total }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import SectorsJson from "@/json/sectors.json";
import moment from "moment";
import { getClaimStatus } from "@/utils/claimStatus";
moment().format();

let agency = JSON.parse(localStorage.getItem("agency"));

export default {
  components: { Navbar, Loading },
  data() {
    return {
      agency: agency,
      user: null,
      claims: { data: [] },
      pagination: { total: 0, per_page: 30, current_page: 1, last_page: 1 },
      selected: "",
      claim_unique_id: "",
      authorization_code: "",
      id_card_number: "",
      stats: "",
      edit: false,
      show: false,
      filterparams: false,
      showsearch: false,
      isLoading: false,
      fullPage: true,
      paginate_value: 30,
      from: moment().subtract(7, "days").format("YYYY-MM-DD"),
      to: moment().format("YYYY-MM-DD"),
      providers: "",
      tpas: "",
      claim_status: "pending",
      org_id: "",
      provider_id: "",
      sector: "",
      sectors: SectorsJson,
      provider_fields: {
        "Provider Name": "agency_name",
      },
      json_fields: {
        "Full Name": "enrollee.full_name",
        "Enrollee ID": "enrollee.id_card_number",
        Diagnosis: "diagnosis.name",
        "Authorization Code": "authorization_code",
        "Requested Amount": "requested_total_cost",
        "Vetted Amount": "vetted_total_cost",
        "Approved / Verified Amount": "approved_total_cost",
        "Date Requested": "created_at",
        "Date Vetted": "checked_by_date",
        "Date Verified": "verified_by_date",
        "Facility Name": "provider.agency_name",
      },
      json_meta: [[{ key: "charset", value: "utf-8" }]],
      viewMode: 'list',
      perfReport: { open: false, loading: false, rows: [] },
      perf_fields: {
        "Employee": "employee_name",
        "Vetted": "vetted",
        "Verified": "verified",
        "Total": "total",
      },
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
  mounted() {
    this.viewMode = window.innerWidth < 768 ? 'grid' : 'list';
  },
  computed: {
    totalRequestedCost() {
      if (this.claims.data) {
        return this.claims.data.reduce((sum, item) => sum + item.requested_total_cost, 0);
      }
      return 0;
    },
    totalApprovedCost() {
      if (this.claims.data) {
        return this.claims.data.reduce((sum, item) => sum + item.approved_total_cost, 0);
      }
      return 0;
    },
  },
  methods: {
    getClaimStatus,

    openClaim(claim) {
      const allIds = this.claims.data.map((c) => c.id).join(",");
      const route = this.$router.resolve({
        path: `/claim/${claim.id}`,
        query: { queue: allIds },
      });
      window.open(route.href, "_blank");
    },

    getClaims(page = 1) {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.isLoading = true;
      this.axios
        .get(`/api/v1/auth/claims/${this.user.type == "nhia" ? null : this.agency.id}`, {
          params: {
            page,
            paginate_value: this.paginate_value,
            claim_status: this.claim_status,
            claim_unique_id: this.claim_unique_id,
            authorization_code: this.authorization_code,
            id_card_number: this.id_card_number,
            provider_id: this.user.type == "provider_employee"
              ? this.user.institutional_id
              : this.provider_id ? this.provider_id.id : null,
            org_id: this.user.type == "tpa_employee" ? this.user.institutional_id : null,
            from: this.from,
            to: this.to,
            sector: this.sector,
          },
        })
        .then((response) => {
          this.stats = response.data;
          this.claims = { data: response.data.data || [] };
          if (response.data.pagination) {
            this.pagination = response.data.pagination;
          }
          this.providers = response.data.providers;
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },

    openPerformanceReport() {
      this.perfReport = { open: true, loading: true, rows: [] };
      this.axios
        .get(`/api/v1/auth/claims/stats-actors`, {
          params: {
            agency_id: this.agency.id,
            sector: this.sector,
            from: this.from,
            to: this.to,
          },
        })
        .then((response) => {
          const data = response.data || {};
          this.perfReport.rows = this.mergeActorRows(data.vetters || [], data.verifiers || []);
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error("Failed to load performance report.", { position: "top-center", duration: 3000 });
        })
        .finally(() => {
          this.perfReport.loading = false;
        });
    },

    closePerformanceReport() {
      this.perfReport.open = false;
    },

    mergeActorRows(vetters, verifiers) {
      const buckets = new Map();
      const ensure = (id, name) => {
        if (!buckets.has(id)) {
          buckets.set(id, { employee_id: id, employee_name: name || `Employee #${id}`, vetted: 0, verified: 0, total: 0 });
        }
        return buckets.get(id);
      };

      vetters.forEach((u) => { ensure(u.user_id, u.name).vetted = u.count || 0; });
      verifiers.forEach((u) => { ensure(u.user_id, u.name).verified = u.count || 0; });

      buckets.forEach((row) => { row.total = row.vetted + row.verified; });

      return Array.from(buckets.values()).sort((a, b) => b.total - a.total);
    },

    getProviders() {
      this.axios
        .get(`/api/v1/auth/providerAgency/${agency.id}`)
        .then((response) => { this.providers = response.data.data; })
        .catch((error) => { console.error(error); });
    },

    forwwardClaims() {
      // Sends the facility, not the claims on this page: the API forwards every verified claim of the facility.
      const name = this.provider_id && this.provider_id.agency_name ? this.provider_id.agency_name : "this facility";
      if (!confirm(`Forward ALL verified claims of ${name} to the ES for approval? This includes claims on other pages.`)) return;
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/forwardclaimtoES`, { provider_id: this.provider_id.id })
        .then((res) => {
          this.isLoading = false;
          this.$toasted.info(res.data.message || "Forwarded successfully!", { position: "top-center", duration: 5000 });
        })
        .catch((error) => {
          this.isLoading = false;
          const d = error.response && error.response.data;
          this.$toasted.error((d && d.message) || "Could not forward the claims.", { position: "top-center", duration: 5000 });
        });
    },

    clearField() {
      this.claim_status = "";
      this.claim_unique_id = "";
      this.authorization_code = "";
      this.id_card_number = "";
      this.provider_id = "";
      this.org_id = "";
      this.sector = "";
      this.getClaims();
    },
  },
  created() {
    this.getClaims();
  },
};
</script>

<style scoped>
/* ── Page Header ── */
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff;
}
.page-title { font-size: 1.6rem; font-weight: 700; margin: 0; color: #fff; }
.page-subtitle { opacity: .75; margin: 4px 0 0; font-size: .9rem; }
.mt-n3 { margin-top: -16px; }

/* ── Stat Cards ── */
.stat-card {
  display: flex; align-items: center; gap: 18px;
  border-radius: 14px; padding: 22px 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,.08);
  color: #fff; height: 100%;
}
.stat-card--blue   { background: linear-gradient(135deg, #1e3a5f, #2563eb); }
.stat-card--purple { background: linear-gradient(135deg, #4c1d95, #7c3aed); }
.stat-card--green  { background: linear-gradient(135deg, #065f46, #10b981); }
.stat-icon {
  width: 52px; height: 52px; border-radius: 12px;
  background: rgba(255,255,255,.2);
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; flex-shrink: 0;
}
.stat-label { font-size: .8rem; opacity: .85; margin-bottom: 4px; font-weight: 500; text-transform: uppercase; letter-spacing: .04em; }
.stat-value { font-size: 1.6rem; font-weight: 700; margin: 0; line-height: 1; }

/* ── Filter Card ── */
.filter-card { border-radius: 12px; border: none; }
.styled-select { border: 1.5px solid #e2e8f0; border-radius: 6px; }

/* ── Claims Table Card ── */
.codes-card { border-radius: 12px; border: none; }
.card-header-custom {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; border-bottom: 1px solid #f1f5f9;
  background: #fff; border-radius: 12px 12px 0 0;
}
.card-header-custom h5 { font-weight: 600; color: #1e3a5f; }

.codes-table { font-size: .9rem; }
.codes-table thead th {
  background: #f8fafc; color: #64748b;
  font-weight: 600; font-size: .75rem;
  text-transform: uppercase; letter-spacing: .06em;
  border-bottom: 2px solid #e2e8f0; padding: 12px 16px;
}
.codes-table tbody td { padding: 12px 16px; vertical-align: middle; border-color: #f1f5f9; }
.code-row:hover { background: #f8fafc; }

/* Status badges */
.status-badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: .75rem; font-weight: 600; text-transform: capitalize;
  margin-bottom: 2px;
}
.status-badge.warning { background: #fef3c7; color: #92400e; }
.status-badge.success { background: #d1fae5; color: #065f46; }
.status-badge.danger  { background: #fee2e2; color: #991b1b; }
.status-badge.indigo  { background: #e0e7ff; color: #3730a3; }
.status-badge.info    { background: #dbeafe; color: #1d4ed8; }
.status-badge.dark    { background: #e5e7eb; color: #1f2937; }
.status-badge.draft { background: #f1f5f9; color: #475569; border: 1px dashed #94a3b8; }
.status-badge.forwarded { background: #ede9fe; color: #5b21b6; }

/* View button */
.btn-view {
  border-radius: 6px; border: 1px solid #e2e8f0;
  color: #1e3a5f; background: #fff; font-size: .8rem;
}
.btn-view:hover { background: #f1f5f9; }

/* Empty state */
.empty-state { padding: 48px !important; color: #94a3b8; }

/* Pagination footer */
.card-footer {
  background: #fff;
  border-top: 1px solid #f1f5f9;
  border-radius: 0 0 12px 12px;
  padding: 12px 24px;
}
.pagination .page-link {
  border-color: #e2e8f0;
  color: #1e3a5f;
  border-radius: 6px !important;
  margin: 0 2px;
  font-size: .82rem;
  padding: 5px 11px;
}
.pagination .page-item.active .page-link {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
.pagination .page-item.disabled .page-link { color: #cbd5e1; }

/* ── View toggle ── */
.view-toggle { display: flex; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.view-toggle-btn { background: none; border: none; padding: 6px 10px; color: #94a3b8; cursor: pointer; font-size: .85rem; line-height: 1; }
.view-toggle-btn.active { background: #1e3a5f; color: #fff; }
.view-toggle-btn:hover:not(.active) { background: #f1f5f9; color: #475569; }

/* ── Grid view ── */
.claims-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  padding: 20px 24px;
}
.claim-grid-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  background: #fff;
  display: flex;
  flex-direction: column;
  transition: border-color .15s, box-shadow .15s;
}
.claim-grid-card:hover { border-color: #93c5fd; box-shadow: 0 2px 10px rgba(37,99,235,.1); }
.grid-card-footer { border-top: 1px solid #f1f5f9; padding-top: 10px; margin-top: auto; }

/* ── Employee Performance Modal ── */
.perf-modal-overlay {
  position: fixed; inset: 0; z-index: 1050;
  background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.perf-modal { width: 100%; max-width: 640px; max-height: 85vh; display: flex; flex-direction: column; }
.perf-modal-body { overflow-y: auto; }
</style>
